import React, { FC, memo, MouseEvent, useMemo } from "react";
import { useDispatch } from "react-redux";

import { getLoopsField, getStopsField } from "Components/common/TableElements";
import { FMSLogger } from "FMSLogger";
import { QUEUE_MANAGER_ACTIONS } from "Utils/constants";
import { cancelLiveAndQueueMission } from "Slices/missionsPage/actions";
import { toggleModal } from "Slices/modals";

import {
  CellButtonStyled,
  CellContainerStyled,
  TableOptionsContainerStyled,
} from "./styles";
import { TableOptions } from "Components/NewList/TableOptions";
import { RowData } from "Components/NewList/types";

type MissionRowProps = {
  mission: Mission;
  vehicleId: string;
};

export const MissionRow: FC<MissionRowProps> = memo(
  ({ mission, vehicleId }) => {
    const dispatch = useDispatch();
    const { name, stops } = mission;

    const handleOpenMissionDetails = (event: MouseEvent<HTMLDivElement>) => {
      if (mission) {
        dispatch(
          toggleModal({
            type: "MISSION_DETAILS_MODAL",
            data: { missionId: mission.id },
          })
        );
        event.stopPropagation();
      }
    };

    const queueManagerActionsLogger = FMSLogger.byPrefix(QUEUE_MANAGER_ACTIONS);

    const options = useMemo(
      () => [
        {
          name: "Cancel",
          dataTestId: "queue-manager-cancel-mission-button",
          callback: (item: RowData) => {
            queueManagerActionsLogger.debug(
              `Pressed cancel mission with id:${item.id}`
            );
            dispatch(
              cancelLiveAndQueueMission({
                missionId: item.id,
                vehicleId: vehicleId,
              })
            );
          },
        },
      ],
      [vehicleId]
    );

    const optionsDropdownOffset = useMemo(() => ({ top: 50, right: -10 }), []);

    // Create a RowData-compatible object from MissionAsset
    const rowData: RowData = useMemo(
      () => ({
        id: mission.id,
        name: mission.name,
        stopsCount: stops.length.toString(),
        loopsCount: (mission.totalLoops || 1).toString(),
      }),
      [mission.id, mission.name, stops.length, mission.totalLoops]
    );

    return (
      <>
        <CellContainerStyled>
          <CellButtonStyled
            data-test-id={`queue-manager-mission-name-${mission.id}`}
            onClickCapture={(event) => handleOpenMissionDetails(event)}
          >
            {name}
          </CellButtonStyled>
        </CellContainerStyled>
        <CellContainerStyled>
          {getStopsField(0, stops.length)}
        </CellContainerStyled>
        <CellContainerStyled>
          {getLoopsField(0, mission.totalLoops || 1)}
          <TableOptionsContainerStyled>
            <TableOptions
              item={rowData}
              options={options}
              optionsDropdownOffset={optionsDropdownOffset}
            />
          </TableOptionsContainerStyled>
        </CellContainerStyled>
      </>
    );
  }
);
